import React from 'react';
import {
  Button,
  CircularProgress,
  LinearProgress,
} from '@material-ui/core';
import SyncIcon from '@material-ui/icons/Sync';
import HistoryIcon from '@material-ui/icons/History';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { formatLastSyncTimeLocalized } from './catalogSyncUtils';
import { useRuntimeIndexing } from './useRuntimeIndexing';
import { isIndexingActive } from './indexingRuntimeUtils';
import styles from './CatalogStatusWidget.module.scss';

const CatalogStatusWidget = () => {
  const history = useHistory();
  const localizeMessage = useLocalizedMessage();
  const currentShop = useSelector(state => state.app?.currentShop);
  const {
    snapshot,
    isLoading,
    isStarting,
    error,
    start,
  } = useRuntimeIndexing({ historyLimit: 20 });
  const running = isIndexingActive(snapshot.current);
  const progress = snapshot.current?.progress;
  const lastSyncTime = formatLastSyncTimeLocalized(snapshot.lastSuccessAt, localizeMessage);

  return (
    <section className={styles.card} data-testid="catalog-status-widget">
      <div className={styles.accent} />
      <header className={styles.header}>
        <div>
          <h2>{localizeMessage('cloud.home.catalog.title')}</h2>
          <p>{localizeMessage('catalog.sync.description')}</p>
        </div>
        <Button
          size="small"
          startIcon={<HistoryIcon />}
          onClick={() => history.push(`/app/${currentShop}/search/catalog-history`)}
        >
          {localizeMessage('catalog.sync.history')}
        </Button>
      </header>

      <div className={styles.content}>
        {isLoading && snapshot.history.length === 0 ? (
          <div className={styles.loading}>
            <CircularProgress size={24} />
            <span>{localizeMessage('catalog.sync.checking')}</span>
          </div>
        ) : (
          <>
            <div className={styles.status} data-status={error ? 'error' : running ? 'running' : 'ready'}>
              {error
                ? <ErrorOutlineIcon />
                : running
                  ? <SyncIcon data-testid="catalog-sync-running-icon" />
                  : <CheckCircleOutlineIcon />}
              <div>
                <strong>{error
                  ? localizeMessage('catalog.sync.status.unavailable')
                  : running
                    ? localizeMessage('catalog.sync.status.running')
                    : localizeMessage('catalog.sync.status.ready')}</strong>
                <span>
                  {error || (running
                    ? localizeMessage('catalog.sync.status.runningDescription')
                    : localizeMessage('catalog.sync.status.lastSuccess', { time: lastSyncTime }))}
                </span>
              </div>
            </div>

            {running && (
              <div className={styles.progress}>
                <div>
                  <span>{localizeMessage('catalog.sync.progress')}</span>
                  {typeof progress === 'number' && <strong>{Math.round(progress)}%</strong>}
                </div>
                <LinearProgress
                  variant={typeof progress === 'number' ? 'determinate' : 'indeterminate'}
                  value={typeof progress === 'number' ? progress : undefined}
                  data-testid="catalog-sync-progress"
                />
              </div>
            )}

            <Button
              variant="contained"
              color="primary"
              startIcon={isStarting ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              onClick={start}
              disabled={isStarting || running}
              fullWidth
              className={styles.syncButton}
              data-testid="catalog-sync-now"
            >
              {isStarting
                ? localizeMessage('catalog.sync.action.starting')
                : running
                  ? localizeMessage('catalog.sync.action.running')
                  : localizeMessage('catalog.sync.action.update')}
            </Button>
          </>
        )}
      </div>
    </section>
  );
};

export default CatalogStatusWidget;
